import React from "react";
import CloseIcon from "@mui/icons-material/Close";

export default function AssignmentsModal({
  showAssignments,
  setShowAssignments,
  assignment = null,
  dayNumber = null,
  themeContext = "light",
}) {
  if (!showAssignments || !assignment || !dayNumber) return null;

  const isDark = themeContext === "dark";

  // Theme-based classes
  const overlayClass = "fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4";

  const modalClass = isDark
    ? "bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 border-slate-700"
    : "bg-gradient-to-br from-white via-gray-50 to-white border-gray-200";

  const headerClass = isDark
    ? "bg-slate-800/50 border-slate-700"
    : "bg-emerald-50 border-emerald-200";

  const titleClass = isDark ? "text-emerald-400" : "text-emerald-700";
  const textPrimaryClass = isDark ? "text-slate-200" : "text-gray-800";
  const textSecondaryClass = isDark ? "text-slate-400" : "text-gray-600";
  const closeButtonClass = isDark
    ? "text-slate-400 hover:text-slate-200 hover:bg-slate-700"
    : "text-gray-500 hover:text-gray-700 hover:bg-gray-100";
  const contentClass = isDark
    ? "bg-slate-900/50 border-slate-700"
    : "bg-gray-50 border-gray-200";

  // Questions Map – extended for day 13 to support two parts
  const questionsMap = {
    13: {
      question:
        "Send me the two explorative essays you've written — one describing your experience during and after the meal, and the other from your stomach's point of view.",
      hasTwoEssays: true,
      label1: "Essay 1:",
      label2: "Essay 2:",
    },
    16: {
      question:
        "'Food As A Person' Explorative Essay. Write essays on the topics: (1) If food were a person, who was it in the past? (2) If food were to become someone new—your ideal relationship—who would it be?",
    },
    21: {
      question: "✍️ Write your own 60–90 second future-food experience script",
    },
    25: {
      question:
        "Write a short script that mentally and emotionally prepares you for real-life success with food.",
    },
    31: {
      question:
        "What have been the wins—big or small—that show how your relationship with food has changed in the past 30 days? If you’ve written them down on your phone as suggested in the morning, copy and paste them here.",
    },
    38: {
      question: `👉 Are you noticing any changes in your relationship with food so far? For example, here are a few changes our users commonly find:
      <br>
“I didn’t feel like having the dessert which I’ve always wanted.”
“I’m no longer feeling the urge of completing the plate now.” 
“I was at a social event and I wasn’t thinking about food.”
<br>
List down the changes you’ve been seeing and feeling. This process helps you rewire your mind to have a different perspective about how food impacts you right now.`,
    },
    39: {
      question:
        "👉 What are the new beliefs I've formed about myself, food, and how I relate to eating since beginning this journey?",
    },
  };

  const currentQuestionData = questionsMap[dayNumber] || {};
  const question = currentQuestionData.question || "No question available for this day.";
  const isDay13 = dayNumber === 13;
  const hasTwoEssays = currentQuestionData.hasTwoEssays === true;

  const formatDate = (dateString) => {
    if (!dateString) return null;
    try {
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return null;
    }
  };

  const submittedDate = formatDate(assignment.createdAt);

  // For day 13 we expect assignment.assignment to be an object { essay1, essay2 }
  // For other days it's a string
  const content = assignment.assignment;

  return (
    <div className={overlayClass} onClick={() => setShowAssignments(false)}>
      <div
        className={`${modalClass} border rounded-2xl shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className={`${headerClass} border-b px-6 py-5 rounded-t-2xl flex items-start justify-between`}
        >
          <div className="flex flex-col gap-3">
            <h2 className={`text-lg font-bold ${titleClass}`}>
              Day {dayNumber} Question:
            </h2>
            <p
              className={`text-base ${textPrimaryClass} whitespace-pre-line leading-relaxed`}
            >
              {question}
            </p>
          </div>
          <button
            onClick={() => setShowAssignments(false)}
            className={`${closeButtonClass} p-2 rounded-full transition-colors`}
          >
            <CloseIcon />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className={`${contentClass} border rounded-lg p-6 space-y-6`}>
            <h3
              className={`text-lg font-semibold ${textPrimaryClass} pb-3 border-b ${
                isDark ? "border-slate-700" : "border-gray-200"
              }`}
            >
              Your Reflection
            </h3>

            {hasTwoEssays && isDay13 ? (
              // ── Day 13: two essay blocks ──
              <div className="space-y-8">
                <div>
                  <h4 className={`font-medium text-base ${textPrimaryClass} mb-2`}>
                    {currentQuestionData.label1 || "Essay 1"}
                  </h4>
                  <div
                    className={`text-sm ${textPrimaryClass} whitespace-pre-wrap leading-relaxed bg-black/5 dark:bg-white/5 p-4 rounded-lg border ${
                      isDark ? "border-slate-700" : "border-gray-200"
                    }`}
                  >
                    {content?.essay1 || "No content provided for Essay 1"}
                  </div>
                </div>

                <div>
                  <h4 className={`font-medium text-base ${textPrimaryClass} mb-2`}>
                    {currentQuestionData.label2 || "Essay 2"}
                  </h4>
                  <div
                    className={`text-sm ${textPrimaryClass} whitespace-pre-wrap leading-relaxed bg-black/5 dark:bg-white/5 p-4 rounded-lg border ${
                      isDark ? "border-slate-700" : "border-gray-200"
                    }`}
                  >
                    {content?.essay2 || "No content provided for Essay 2"}
                  </div>
                </div>
              </div>
            ) : (
              // ── Normal days: single block ──
              <div
                className={`text-sm ${textPrimaryClass} whitespace-pre-wrap leading-relaxed bg-black/5 dark:bg-white/5 p-5 rounded-lg border ${
                  isDark ? "border-slate-700" : "border-gray-200"
                }`}
              >
                {typeof content === "string"
                  ? content
                  : "No reflection submitted."}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className={`${headerClass} border-t px-6 py-4 rounded-b-2xl`}>
          <div className="flex items-center justify-center gap-2 text-sm">
            <span className={textSecondaryClass}>Submitted on:</span>
            <span className={`font-semibold ${textPrimaryClass}`}>
              {submittedDate || "Unknown date"}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}